import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";

import { api } from "@/api/client";
import { intlLocale } from "@/i18n/format";

/** A sign-in method, as ``GET /api/auth/providers`` lists it. */
export type AuthProvider = {
  name: "google" | "microsoft" | "github" | "ldap" | "dev-bypass";
  display_name: string;
  kind: "oauth" | "password" | "dev";
  login_url: string;
};

/** Product names, the same in every language. */
const BRAND: Readonly<Partial<Record<string, string>>> = {
  google: "Google",
  microsoft: "Microsoft",
  github: "GitHub",
  ldap: "LDAP",
};

/** The sign-in methods this deployment offers. Public, like the login
 *  page that reads it; shared by the landing page through the cache. */
export function useAuthProviders() {
  return useQuery({
    queryKey: ["auth-providers"],
    queryFn: async () => (await api.get<AuthProvider[]>("/auth/providers")).data,
    retry: false,
  });
}

/** A sign-in method's label in the UI language. The backend's
 *  ``display_name`` is English: it stays the label only for a provider
 *  this build doesn't know and for the development-only bypass. */
export function providerLabel(t: TFunction, provider: AuthProvider): string {
  const brand = BRAND[provider.name];
  return brand ? t("auth.sign_in_with", { provider: brand }) : provider.display_name;
}

/** "Google, Microsoft or LDAP" in the UI language: the sign-in methods a
 *  visitor can pick, without the development-only bypass. ``null`` when
 *  there is none to name. */
export function providerList(providers: readonly AuthProvider[]): string | null {
  const brands = providers
    .map((p) => BRAND[p.name])
    .filter((b): b is string => b !== undefined);
  if (brands.length === 0) return null;
  return new Intl.ListFormat(intlLocale(), { style: "long", type: "disjunction" }).format(brands);
}
